import { Plus, Search, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useDebouncedSearch } from '@/lib/hooks/use-debounced-search';

import { PROJECT_SORT_OPTIONS, type ProjectSort } from './projects-search';

export interface ProjectsToolbarProps {
  /** The committed search term, straight from the URL. */
  search: string;
  sort: ProjectSort;
  onSearchChange: (search: string) => void;
  onSortChange: (sort: ProjectSort) => void;
  onCreate: () => void;
  /** Whether the current role may create a project (spec matrix). */
  canCreate: boolean;
  /** True while a request is in flight, for the input's busy state. */
  isFetching: boolean;
}

/**
 * Search, sort and create for the project list (spec R6.5).
 *
 * The input is debounced against the URL rather than against the request: with
 * the URL as the source of truth, a filtered view is a link somebody can send,
 * and the query follows from it instead of the other way round.
 */
export const ProjectsToolbar = ({
  search,
  sort,
  onSearchChange,
  onSortChange,
  onCreate,
  canCreate,
  isFetching,
}: ProjectsToolbarProps) => {
  const [draft, setDraft, clearDraft] = useDebouncedSearch(search, onSearchChange);

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
      <div className="relative w-full sm:max-w-xs">
        <Search
          aria-hidden="true"
          strokeWidth={1.75}
          className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
        />
        <Input
          type="search"
          value={draft}
          onChange={(event) => {
            setDraft(event.target.value);
          }}
          placeholder="Search projects"
          aria-label="Search projects by name or description"
          aria-busy={isFetching}
          className="pl-9 pr-9 [&::-webkit-search-cancel-button]:appearance-none"
        />
        {draft === '' ? null : (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={clearDraft}
            className="absolute right-1 top-1/2 size-7 -translate-y-1/2 text-muted-foreground hover:text-foreground"
          >
            <X aria-hidden="true" />
            <span className="sr-only">Clear search</span>
          </Button>
        )}
      </div>

      <Select
        value={sort}
        onValueChange={(value) => {
          onSortChange(value as ProjectSort);
        }}
      >
        <SelectTrigger className="w-full sm:w-48" aria-label="Sort projects">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {PROJECT_SORT_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {canCreate ? (
        <Button type="button" onClick={onCreate} className="sm:ml-auto">
          <Plus aria-hidden="true" />
          New project
        </Button>
      ) : null}
    </div>
  );
};
